import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const API_KEY = "AIzaSyAwN-reyIdTnS6c4GxMBWCY09sikLdkBko";
const CX = "87ed30350e7594aa8";

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::placeholder { color: rgba(148,163,184,0.4); }
  input { outline: none; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #0f0f1a; }
  ::-webkit-scrollbar-thumb { background: #4a6cf7; border-radius: 10px; }
`;
document.head.appendChild(styleSheet);

const miniImages = [
  "https://upload.wikimedia.org/wikipedia/commons/c/c3/Python-logo-notext.svg",
  "https://upload.wikimedia.org/wikipedia/commons/4/4c/JavaScript_Logo.png",
  "https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg",
  "https://upload.wikimedia.org/wikipedia/commons/6/64/Artificial_Intelligence_logo.svg",
  "https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg",
];

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div style={{
    width: "20px", height: "20px",
    border: "2px solid rgba(255,255,255,0.2)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  }} />
);

// ─── Badge PDF ────────────────────────────────────────────────────────────────
const PdfBadge = () => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: "5px",
    fontSize: "10px", fontWeight: "800", letterSpacing: "1px",
    color: "#818cf8",
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.2)",
    padding: "4px 10px", borderRadius: "8px",
    marginBottom: "16px", width: "fit-content",
  }}>
    <span>📄</span> PDF
  </div>
);

// ─── Card Résultat ────────────────────────────────────────────────────────────
const ResultCard = ({ result, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.08, duration: 0.5 }}
    whileHover={{ y: -6, transition: { duration: 0.2 } }}
    style={{
      background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
      backdropFilter: "blur(20px)",
      borderRadius: "20px",
      padding: "28px",
      border: "1px solid rgba(255,255,255,0.07)",
      display: "flex", flexDirection: "column",
      position: "relative", overflow: "hidden",
      cursor: "pointer",
    }}
  >
    {/* Ligne décorative en haut */}
    <div style={{
      position: "absolute", top: 0, left: "20%", right: "20%", height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent)",
    }} />

    <PdfBadge />

    <h3 style={{
      fontSize: "16px", fontWeight: "700",
      color: "#f1f5f9", marginBottom: "12px", lineHeight: "1.5",
      display: "-webkit-box", WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical", overflow: "hidden",
    }}>
      {result.title}
    </h3>

    <p style={{
      fontSize: "13px", color: "#64748b",
      lineHeight: "1.7", flex: 1, marginBottom: "24px",
      display: "-webkit-box", WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical", overflow: "hidden",
    }}>
      {result.snippet}
    </p>

    <motion.a
      href={result.link}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        padding: "12px", borderRadius: "12px",
        background: "linear-gradient(135deg, rgba(74,108,247,0.2), rgba(99,102,241,0.1))",
        border: "1px solid rgba(99,102,241,0.25)",
        color: "#818cf8", fontWeight: "600", fontSize: "14px",
        textDecoration: "none", transition: "all 0.3s",
      }}
    >
      <span>Ouvrir le PDF</span>
      <span style={{ fontSize: "16px" }}>→</span>
    </motion.a>
  </motion.div>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ searched }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    style={{ textAlign: "center", padding: "80px 20px" }}
  >
    <div style={{ fontSize: "60px", marginBottom: "20px" }}>
      {searched ? "🔍" : "📚"}
    </div>
    <p style={{ color: "#334155", fontSize: "16px", fontWeight: "500" }}>
      {searched
        ? "Aucun résultat trouvé. Essayez avec d'autres mots-clés."
        : "Recherchez un cours, une technologie, un langage..."}
    </p>
  </motion.div>
);

// ─── Composant Principal ──────────────────────────────────────────────────────
function Cours() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const handleGoBack = () => navigate("/", { replace: true });

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(
        `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(
          query + " filetype:pdf cours"
        )}&key=${API_KEY}&cx=${CX}`
      );
      const data = await res.json();
      setResults(
        data.items
          ? data.items.map((item) => ({
              title: item.title,
              link: item.link,
              snippet: item.snippet,
            }))
          : []
      );
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#080812",
      fontFamily: "'Inter', sans-serif",
      padding: "0 24px 100px",
      position: "relative",
      overflowX: "hidden",
      color: "#fff",
    }}>

      {/* ── Orbes de fond ── */}
      <div style={{
        position: "fixed", top: "-20%", right: "-10%",
        width: "600px", height: "600px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(74,108,247,0.12) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0,
      }} />
      <div style={{
        position: "fixed", bottom: "-10%", left: "-10%",
        width: "500px", height: "500px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0,
      }} />

      {/* ── Logos animés ── */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.img
            key={i}
            src={miniImages[i % miniImages.length]}
            alt=""
            style={{
              position: "absolute",
              width: "40px", opacity: 0.05,
              top: `${(i * 7 + 10) % 100}%`,
              left: `${(i * 13 + 5) % 100}%`,
              filter: "brightness(0) invert(1)",
            }}
            animate={{ y: [0, -500], rotate: 360 }}
            transition={{ duration: 30 + i * 2, repeat: Infinity, ease: "linear", delay: i }}
          />
        ))}
      </div>

      {/* ── Contenu ── */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", position: "relative", zIndex: 10 }}>

        {/* ── Nav ── */}
        <div style={{ padding: "36px 0 60px" }}>
          <motion.button
            whileHover={{ scale: 1.04, background: "rgba(255,255,255,0.08)" }}
            whileTap={{ scale: 0.96 }}
            onClick={handleGoBack}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "10px 20px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "12px", color: "#94a3b8",
              cursor: "pointer", fontSize: "14px", fontWeight: "500",
              backdropFilter: "blur(10px)", transition: "all 0.3s",
            }}
          >
            ← Accueil
          </motion.button>
        </div>

        {/* ── Hero ── */}
        <div style={{ textAlign: "center", marginBottom: "60px" }}>

          {/* Tag */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              padding: "6px 16px", borderRadius: "100px",
              background: "rgba(99,102,241,0.1)",
              border: "1px solid rgba(99,102,241,0.2)",
              color: "#818cf8", fontSize: "13px", fontWeight: "600",
              marginBottom: "24px",
            }}
          >
            <span style={{ fontSize: "16px" }}>📖</span>
            Ressources pédagogiques
          </motion.div>

          {/* Titre */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              fontSize: "clamp(36px, 6vw, 64px)",
              fontWeight: "900", letterSpacing: "-2px",
              lineHeight: "1.1", marginBottom: "20px",
            }}
          >
            Bibliothèque{" "}
            <span style={{
              background: "linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #4a6cf7 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "shimmer 3s linear infinite",
            }}>
              HDC
            </span>
          </motion.h1>

          {/* Sous-titre */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{ color: "#475569", fontSize: "17px", lineHeight: "1.7", maxWidth: "520px", margin: "0 auto" }}
          >
            Accédez instantanément à des milliers de ressources PDF sélectionnées,
            sans vous perdre sur Internet.
          </motion.p>
        </div>

        {/* ── Barre de recherche ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            maxWidth: "720px", margin: "0 auto 80px",
            background: "rgba(255,255,255,0.03)",
            backdropFilter: "blur(30px)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "20px", padding: "8px",
            display: "flex", gap: "8px",
            boxShadow: "0 30px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.05)",
          }}
        >
          {/* Icône */}
          <div style={{
            display: "flex", alignItems: "center",
            paddingLeft: "16px", color: "#334155", fontSize: "18px",
          }}>
            🔍
          </div>

          {/* Input */}
          <input
            type="text"
            placeholder="Rechercher une technologie, un cours..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            style={{
              flex: 1, background: "transparent", border: "none",
              padding: "16px 12px", color: "#f1f5f9", fontSize: "15px",
              fontFamily: "inherit",
            }}
          />

          {/* Bouton */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSearch}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: "120px", padding: "14px 28px",
              background: "linear-gradient(135deg, #4a6cf7, #6366f1)",
              border: "none", borderRadius: "14px",
              color: "#fff", fontWeight: "700", fontSize: "14px",
              cursor: "pointer", fontFamily: "inherit",
              boxShadow: "0 8px 24px rgba(74,108,247,0.35)",
            }}
          >
            {loading ? <Spinner /> : "Explorer →"}
          </motion.button>
        </motion.div>

        {/* ── Stats (si résultats) ── */}
        <AnimatePresence>
          {results.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              style={{
                display: "flex", alignItems: "center", gap: "12px",
                marginBottom: "32px",
              }}
            >
              <div style={{
                height: "1px", flex: 1,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06))",
              }} />
              <span style={{ color: "#334155", fontSize: "13px", fontWeight: "500", whiteSpace: "nowrap" }}>
                {results.length} ressource{results.length > 1 ? "s" : ""} trouvée{results.length > 1 ? "s" : ""}
              </span>
              <div style={{
                height: "1px", flex: 1,
                background: "linear-gradient(90deg, rgba(255,255,255,0.06), transparent)",
              }} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Grille résultats ── */}
        {results.length > 0 ? (
          <motion.div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "20px",
            }}
          >
            <AnimatePresence>
              {results.map((r, i) => (
                <ResultCard key={i} result={r} index={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <EmptyState searched={searched} />
        )}
      </div>
    </div>
  );
}

export default Cours;
